// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { useToast } from '@/contexts/toast-context';
import { Box } from '@mui/material';
import { AxiosError } from 'axios';

const RESYNC_TOAST_DURATION_MS = 5000;

// Backend error codes (constants.ErrorCode) that get a specific operator-facing resync
// toast instead of the generic support snackbar. Only the provider-unreachable code is
// registered in ERROR_CODE_MAPPING; the others are intentionally not FE-mapped so the
// backend's SKU/serial-aware message reaches the toast instead of a static override.
const DUPLICATE_SERIALS_CODE = 2217; // ErrorCodeInvalidProductReceiveDuplicateSerials
const SERIAL_COUNT_MISMATCH_CODE = 2264; // ErrorCodeInvalidProductReceiveSerialCountMismatch
const PRODUCT_TRANSMUTATION_CODE = 5023; // ErrorCodeProductTransmutation
const RESYNC_TOO_LARGE_CODE = 2292; // ErrorCodeFulfillmentOrderResyncTooLarge
const RESYNC_TOO_SOON_CODE = 2293; // ErrorCodeFulfillmentOrderResyncTooSoon
const RESYNC_TOAST_ERROR_CODES: ReadonlySet<number> = new Set([
  ERROR_CODE_MAPPING.third_party_provider_unreachable,
  DUPLICATE_SERIALS_CODE,
  SERIAL_COUNT_MISMATCH_CODE,
  PRODUCT_TRANSMUTATION_CODE,
  RESYNC_TOO_LARGE_CODE,
  RESYNC_TOO_SOON_CODE,
]);

/**
 * Returns a handler that shows a 5-second, operator-facing toast for the known 3PL
 * resync errors using the backend-provided (SKU/serial-aware) message. Returns true
 * when it handled the error, or false when the caller should fall back to its generic
 * error handling.
 */
export function useResyncErrorToast() {
  const { showToast } = useToast();

  return (error: unknown): boolean => {
    const apiError = handleAPIError(error as AxiosError);
    if (!apiError || !RESYNC_TOAST_ERROR_CODES.has(apiError.apiStatusCode)) {
      return false;
    }
    showToast({
      severity: 'error',
      duration: RESYNC_TOAST_DURATION_MS,
      element: <Box>{apiError.message}</Box>,
    });
    return true;
  };
}
